import { html, css } from 'lit';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import { SsoElement } from './sso-base.js';

/**
 * `<sso-code-input>` — split N-box numeric code entry (default 6) for TOTP / recovery (§5.4).
 *
 * - Auto-advances on entry, backspaces to the previous box, supports arrow-key navigation.
 * - **Paste-aware**: pasting the full code into any box distributes the digits across all boxes
 *   (mobile OS one-time-code autofill).
 * - `inputmode="numeric"` + `autocomplete="one-time-code"` so the numeric keypad shows and the
 *   OS offers SMS/authenticator autofill.
 * - Emits `complete` (CustomEvent<string>) when all boxes are filled, and `code-change` on any edit.
 * - A single `aria-label` describes the whole group (set by the host via the `aria-label` attribute).
 */
@customElement('sso-code-input')
export class SsoCodeInput extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
      }
      .boxes {
        display: flex;
        gap: 0.4rem;
        justify-content: center;
      }
      /* visual gap between the two triplets, as in the mockups */
      .gap {
        width: 0.6rem;
      }
      input {
        font: var(--sso-font-mono);
        font-size: 1.5rem;
        width: 2.6rem;
        height: 3.2rem;
        text-align: center;
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        background: var(--sso-color-surface);
        color: var(--sso-color-fg);
        caret-color: var(--sso-color-brand);
      }
      input:focus-visible {
        outline: none;
        border-color: var(--sso-color-brand);
        box-shadow: var(--sso-focus-ring);
      }
      :host([invalid]) input {
        border-color: var(--sso-color-danger);
      }
      @media (max-width: 380px) {
        input {
          width: 2.1rem;
          height: 2.8rem;
          font-size: 1.25rem;
        }
      }
    `,
  ];

  @property({ type: Number }) length = 6;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ attribute: 'aria-label' }) ariaLabelText = '';

  @state() private digits: string[] = [];
  @queryAll('input') private inputs!: NodeListOf<HTMLInputElement>;

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.digits.length !== this.length) {
      this.digits = Array.from({ length: this.length }, () => '');
    }
  }

  /** Focus the first box (used by pages for first-field focus on entry). */
  override focus(): void {
    void this.updateComplete.then(() => this.inputs[0]?.focus());
  }

  /** Clear all boxes and refocus the first (used after a failed verify). */
  reset(): void {
    this.digits = Array.from({ length: this.length }, () => '');
    this.invalid = false;
    void this.updateComplete.then(() => this.inputs[0]?.focus());
  }

  private get value(): string {
    return this.digits.join('');
  }

  private emit(): void {
    this.dispatchEvent(new CustomEvent<string>('code-change', { detail: this.value }));
    if (this.value.length === this.length && this.digits.every((d) => d !== '')) {
      this.dispatchEvent(
        new CustomEvent<string>('complete', { detail: this.value, bubbles: true, composed: true }),
      );
    }
  }

  private onInput(i: number, e: InputEvent): void {
    const el = e.target as HTMLInputElement;
    const ch = el.value.replace(/\D/g, '').slice(-1); // keep last numeric char only
    const next = [...this.digits];
    next[i] = ch;
    this.digits = next;
    el.value = ch;
    if (ch && i < this.length - 1) this.inputs[i + 1]?.focus();
    this.emit();
  }

  private onKeyDown(i: number, e: KeyboardEvent): void {
    if (e.key === 'Backspace') {
      if (this.digits[i]) {
        const next = [...this.digits];
        next[i] = '';
        this.digits = next;
        this.emit();
      } else if (i > 0) {
        this.inputs[i - 1]?.focus();
        const next = [...this.digits];
        next[i - 1] = '';
        this.digits = next;
        this.emit();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      this.inputs[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && i < this.length - 1) {
      this.inputs[i + 1]?.focus();
      e.preventDefault();
    }
  }

  private onPaste(e: ClipboardEvent): void {
    const text = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    const next = Array.from({ length: this.length }, (_, k) => text[k] ?? '');
    this.digits = next;
    void this.updateComplete.then(() => {
      const lastFilled = Math.min(text.length, this.length) - 1;
      this.inputs[Math.max(0, lastFilled)]?.focus();
    });
    this.emit();
  }

  override render() {
    return html`
      <div
        class="boxes"
        role="group"
        aria-label=${this.ariaLabelText}
        @paste=${this.onPaste}>
        ${Array.from({ length: this.length }, (_, i) => {
          // Insert a visual gap halfway (e.g. between the two triplets of a 6-digit code).
          const gap = this.length === 6 && i === 3 ? html`<span class="gap"></span>` : '';
          return html`
            ${gap}
            <input
              type="text"
              inputmode="numeric"
              autocomplete=${i === 0 ? 'one-time-code' : 'off'}
              maxlength="1"
              .value=${this.digits[i] ?? ''}
              ?disabled=${this.disabled}
              aria-label=${`${i + 1}`}
              @input=${(e: InputEvent) => this.onInput(i, e)}
              @keydown=${(e: KeyboardEvent) => this.onKeyDown(i, e)} />
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-code-input': SsoCodeInput;
  }
}
